import { createContext, useContext, useState, useEffect, useCallback } from "react";

const SidebarContext = createContext(null);

/**
 * وضع القائمة الجانبية حسب المساحة المتاحة (لا اسم الجهاز) — نفس حدود styles/theme.css:
 *  - desktop  ≥ 1280px : كاملة ومفتوحة افتراضياً، تدفع المحتوى.
 *  - laptop   1024–1279: مضغوطة (أيقونات) افتراضياً، وتتسع عند الطلب وتدفع المحتوى.
 *  - tablet    768–1023: شريط أيقونات؛ فتحها يعرضها فوق المحتوى مع خلفية معتمة.
 *  - mobile      < 768 : مخفية (Drawer)؛ تُفتح من زر القائمة في الهيدر فوق المحتوى.
 */
const BP = { mobile: 767.98, tablet: 1023.98, laptop: 1279.98 };

function modeOf(width) {
  if (width <= BP.mobile) return "mobile";
  if (width <= BP.tablet) return "tablet";
  if (width <= BP.laptop) return "laptop";
  return "desktop";
}

const defaultOpen = (mode) => mode === "desktop";

export function SidebarProvider({ children }) {
  const [mode, setMode] = useState(() => (typeof window === "undefined" ? "desktop" : modeOf(window.innerWidth)));
  const [open, setOpen] = useState(() => defaultOpen(mode));

  useEffect(() => {
    let current = mode;
    const onResize = () => {
      const next = modeOf(window.innerWidth);
      if (next !== current) {
        current = next;
        setMode(next);
        setOpen(defaultOpen(next)); // كل وضع يبدأ بحالته الطبيعية
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** القائمة تطفو فوق المحتوى (الجوال واللوحي) بدل أن تدفعه. */
  const overlay = mode === "mobile" || mode === "tablet";

  // منع تمرير الصفحة خلف القائمة المفتوحة فوق المحتوى، و Esc يغلقها
  useEffect(() => {
    const lock = overlay && open;
    document.body.classList.toggle("asf-scroll-lock", lock);
    if (!lock) return () => document.body.classList.remove("asf-scroll-lock");
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("asf-scroll-lock");
      window.removeEventListener("keydown", onKey);
    };
  }, [overlay, open]);

  const toggle = useCallback(() => setOpen((prev) => !prev), []);
  /** يُغلق القائمة الطافية بعد اختيار صفحة (لا أثر لها في وضع سطح المكتب). */
  const closeIfOverlay = useCallback(() => {
    if (overlay) setOpen(false);
  }, [overlay]);

  return (
    <SidebarContext.Provider value={{ open, setOpen, toggle, mode, overlay, closeIfOverlay }}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebar لازم يتستخدم جوه SidebarProvider");
  return ctx;
}
